import { useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { ParticipantEvent, RoomEvent } from "livekit-client";
import * as React from "react";
import { resolveParticipantDisplayName, toFirstName } from "./liveInterviewDisplayUtils";

export const LI_HAND_RAISED_META_KEY = "liHandRaised";
export const REACTION_TOPIC = "li-meeting-reaction";

const RaiseHandContext = React.createContext(null);

export function parseParticipantHandRaised(metadata) {
  if (!metadata) return false;
  try {
    const parsed = JSON.parse(metadata);
    return Boolean(parsed?.[LI_HAND_RAISED_META_KEY] ?? parsed?.liHandRaised);
  } catch {
    return false;
  }
}

export function parseRaiseHandDataPayload(payload) {
  try {
    const text = new TextDecoder().decode(payload);
    const data = JSON.parse(text);
    if (data?.v !== 1 || data?.kind !== "raise_hand") return null;
    return {
      sender: String(data.sender || "").trim(),
      name: String(data.name || "").trim(),
      raised: String(data.value || "").trim().toLowerCase() === "raised",
    };
  } catch {
    return null;
  }
}

export function buildHandRaisedMetadata(raised, existingMetadata = "") {
  let base = {};
  if (existingMetadata) {
    try {
      const parsed = JSON.parse(existingMetadata);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        base = parsed;
      }
    } catch {
      base = {};
    }
  }
  return JSON.stringify({
    ...base,
    [LI_HAND_RAISED_META_KEY]: Boolean(raised),
  });
}

function resolveRoomParticipant(room, identity) {
  if (!room || !identity) return null;
  if (room.localParticipant?.identity === identity) {
    return room.localParticipant;
  }
  return room.getParticipantByIdentity(identity) || null;
}

function resolveParticipantLabel(room, identity, fallbackName = "") {
  const cleanFallback = String(fallbackName || "").trim();
  if (cleanFallback) return cleanFallback;
  const participant = resolveRoomParticipant(room, identity);
  if (!participant) return "Participant";
  return resolveParticipantDisplayName(participant) || "Participant";
}

async function publishRaiseHand(room, localParticipant, value, displayName) {
  if (!room?.localParticipant || !localParticipant) return;
  const name = resolveParticipantLabel(room, localParticipant.identity, displayName);
  const body = JSON.stringify({
    v: 1,
    name,
    sender: localParticipant.identity || "",
    kind: "raise_hand",
    value,
  });
  await room.localParticipant.publishData(new TextEncoder().encode(body), {
    reliable: true,
    topic: REACTION_TOPIC,
  });
}

function syncRaisedFromParticipant(room, participant, applyState) {
  if (!participant?.identity) return;
  const raised = parseParticipantHandRaised(participant.metadata);
  applyState(
    participant.identity,
    raised,
    resolveParticipantDisplayName(participant),
    participant.identity === room?.localParticipant?.identity,
  );
}

export function LiveInterviewRaiseHandProvider({ children }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const localIdentity = localParticipant?.identity || "";
  const [raisedByIdentity, setRaisedByIdentity] = React.useState(() => new Map());

  const applyHandState = React.useCallback((identity, raised, name, isLocal = false) => {
    if (!identity) return;
    setRaisedByIdentity((prev) => {
      const next = new Map(prev);
      if (raised) {
        next.set(identity, {
          name: String(name || "Participant").trim() || "Participant",
          isLocal: Boolean(isLocal),
        });
      } else {
        next.delete(identity);
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!room) return undefined;
    const participantHandlers = new Map();

    const attachParticipant = (participant) => {
      if (!participant?.identity) return;
      const existing = participantHandlers.get(participant.identity);
      if (existing) {
        participant.off(ParticipantEvent.ParticipantMetadataChanged, existing);
      }
      const handler = () => {
        applyHandState(
          participant.identity,
          parseParticipantHandRaised(participant.metadata),
          resolveParticipantDisplayName(participant),
          participant.identity === room.localParticipant?.identity,
        );
      };
      participant.on(ParticipantEvent.ParticipantMetadataChanged, handler);
      participantHandlers.set(participant.identity, handler);
      syncRaisedFromParticipant(room, participant, applyHandState);
    };

    const detachParticipant = (participant) => {
      if (!participant?.identity) return;
      const handler = participantHandlers.get(participant.identity);
      if (handler) {
        participant.off(ParticipantEvent.ParticipantMetadataChanged, handler);
        participantHandlers.delete(participant.identity);
      }
    };

    const onRoomMetadataChanged = (_prevMetadata, participant) => {
      if (!participant?.identity) return;
      applyHandState(
        participant.identity,
        parseParticipantHandRaised(participant.metadata),
        resolveParticipantDisplayName(participant),
        participant.identity === room.localParticipant?.identity,
      );
    };

    const onDataReceived = (payload, sender, _kind, topic) => {
      if (topic && topic !== REACTION_TOPIC) return;
      const parsed = parseRaiseHandDataPayload(payload);
      if (!parsed?.sender) return;
      applyHandState(
        parsed.sender,
        parsed.raised,
        resolveParticipantLabel(room, parsed.sender, parsed.name),
        parsed.sender === room.localParticipant?.identity,
      );
    };

    const onParticipantConnected = (participant) => {
      attachParticipant(participant);
    };

    const onParticipantDisconnected = (participant) => {
      detachParticipant(participant);
      if (participant?.identity) {
        applyHandState(participant.identity, false);
      }
    };

    room.on(RoomEvent.ParticipantMetadataChanged, onRoomMetadataChanged);
    room.on(RoomEvent.DataReceived, onDataReceived);
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);

    attachParticipant(room.localParticipant);
    room.remoteParticipants.forEach((participant) => attachParticipant(participant));

    return () => {
      room.off(RoomEvent.ParticipantMetadataChanged, onRoomMetadataChanged);
      room.off(RoomEvent.DataReceived, onDataReceived);
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
      participantHandlers.forEach((handler, identity) => {
        const participant = resolveRoomParticipant(room, identity);
        participant?.off(ParticipantEvent.ParticipantMetadataChanged, handler);
      });
      participantHandlers.clear();
    };
  }, [applyHandState, localIdentity, room]);

  const isHandRaised = React.useCallback(
    (identity) => {
      if (!identity) return false;
      return raisedByIdentity.has(identity);
    },
    [raisedByIdentity],
  );

  const toggleLocalHand = React.useCallback(async () => {
    if (!room || !localParticipant) return;
    const next = !isHandRaised(localParticipant.identity);
    const displayName = resolveParticipantDisplayName(localParticipant);
    applyHandState(localParticipant.identity, next, displayName, true);
    try {
      await publishRaiseHand(
        room,
        localParticipant,
        next ? "raised" : "lowered",
        displayName,
      );
      try {
        await localParticipant.setMetadata(
          buildHandRaisedMetadata(next, localParticipant.metadata),
        );
      } catch (metadataError) {
        console.warn("[LiveInterview] Raise hand metadata sync failed", metadataError);
      }
    } catch (error) {
      applyHandState(localParticipant.identity, !next, displayName, true);
      console.warn("[LiveInterview] Failed to update raise hand", error);
    }
  }, [applyHandState, isHandRaised, localParticipant, room]);

  const value = React.useMemo(
    () => ({
      raisedByIdentity,
      localIdentity,
      isHandRaised,
      toggleLocalHand,
    }),
    [isHandRaised, localIdentity, raisedByIdentity, toggleLocalHand],
  );

  return (
    <RaiseHandContext.Provider value={value}>{children}</RaiseHandContext.Provider>
  );
}

export function useRaiseHandContext() {
  return React.useContext(RaiseHandContext);
}

export function useParticipantHandRaised(participant) {
  const context = useRaiseHandContext();
  const identity = participant?.identity || "";
  const [metadataRaised, setMetadataRaised] = React.useState(() =>
    parseParticipantHandRaised(participant?.metadata),
  );

  React.useEffect(() => {
    setMetadataRaised(parseParticipantHandRaised(participant?.metadata));
  }, [participant?.metadata, identity]);

  if (context && identity) {
    return context.isHandRaised(identity);
  }

  return metadataRaised;
}

export function LiveInterviewRaiseHandBanner() {
  const context = useRaiseHandContext();
  if (!context) return null;

  const entries = Array.from(context.raisedByIdentity.entries());
  if (!entries.length) return null;

  return (
    <div className="li-raise-hand-banner" role="status" aria-live="polite">
      {entries.map(([identity, { name }]) => {
        const label =
          identity === context.localIdentity
            ? "You raised your hand"
            : `${toFirstName(name) || name || "Someone"} raised their hand`;
        return (
          <div key={identity} className="li-raise-hand-banner-item">
            {label}
          </div>
        );
      })}
    </div>
  );
}
